import { useDebounceFn } from '@vueuse/core'

import { persistReportGraphsDrawerWidth } from '@app/shared/api/user'
import {
  REPORT_GRAPHS_DRAWER_MIN_WIDTH_PCT,
  REPORT_GRAPHS_DRAWER_MAX_WIDTH_PCT,
  REPORT_GRAPHS_DRAWER_MIN_WIDTH_PX,
  REPORT_ROW_SELECTOR,
} from '@app/application/report/constants'

// Persistence fires once per drag (on drop) and is fire-and-forget, so this only
// coalesces rapid successive drags. It never delays the live, on-pointermove resize.
const PERSIST_DEBOUNCE_MS = 150

export function clampWidthPct(pct: number): number {
  return Math.min(
    REPORT_GRAPHS_DRAWER_MAX_WIDTH_PCT,
    Math.max(REPORT_GRAPHS_DRAWER_MIN_WIDTH_PCT, Math.round(pct))
  )
}

export const schedulePersistWidthPct = useDebounceFn((pct: number) => {
  void persistReportGraphsDrawerWidth(pct)
}, PERSIST_DEBOUNCE_MS)

// The row the sidebar is a percentage of. Looked up with `closest` rather than
// `parentElement`: the Vue mount wrapper sits between the two and has no box
// of its own (`display: contents`). Null while the pane is not laid out.
function rowWidth(pane: HTMLElement | null): number | null {
  const row = pane?.closest<HTMLElement>(REPORT_ROW_SELECTOR)
  if (!row || row.clientWidth === 0) return null
  return row.clientWidth
}

// The smallest percent a drag may reach in this row: the stored minimum, or
// more when that percent would render under the pixel floor. Null while the
// pane is not laid out.
export function minWidthPct(pane: HTMLElement | null): number | null {
  const width = rowWidth(pane)
  if (width === null) return null
  return Math.max(
    REPORT_GRAPHS_DRAWER_MIN_WIDTH_PCT,
    Math.ceil((REPORT_GRAPHS_DRAWER_MIN_WIDTH_PX / width) * 100)
  )
}

// The pane's rendered width as a whole percent of the row, or null while the
// pane is not laid out.
export function readWidthPct(pane: HTMLElement | null): number | null {
  const width = rowWidth(pane)
  if (!pane || width === null) return null
  return Math.round((pane.getBoundingClientRect().width / width) * 100)
}

// Snapshots the drag geometry, the pane's left edge and the row width, both
// stable while a drag lasts, so the returned converter is pure math with no
// layout reads on pointermove.
export function beginDragMeasurement(
  pane: HTMLElement | null
): ((clientX: number) => number) | null {
  const width = rowWidth(pane)
  const floor = minWidthPct(pane)
  if (!pane || width === null || floor === null) return null
  const paneLeft = pane.getBoundingClientRect().left
  return (clientX) =>
    Math.max(floor, clampWidthPct(((clientX - paneLeft) / width) * 100))
}
